<script lang="ts">
  import { INK_TOKENS, resolveInk } from '../theme/tokens';
  import { app } from './appState.svelte';
  import type { InkEngine } from '../ink/engine';
  import type { Rect } from '../ink/stroke';

  let { engine }: { engine: InkEngine } = $props();

  // Raw width presets (same set as PenPopover). Applied as-is to every
  // selected stroke — highlighter strokes get the raw value too (no ×4
  // pre-multiply); acceptable v1 simplification.
  const WIDTHS = [1, 2, 3, 5, 8, 12];
  /** Screen px the bar floats above the bbox top (clears the rotate handle). */
  const BAR_LIFT = 96;

  let sel = $state<{ kind: 'strokes' | 'image'; count: number; bboxWorld: Rect } | null>(null);
  let vp = $state<{ x: number; y: number; scale: number }>({ x: 0, y: 0, scale: 1 });
  let showRestyle = $state(false);

  // (Re)subscribe per engine instance. The engine has no off(); a replaced
  // engine is always destroyed by the Editor, so stale listeners are inert
  // (destroy's commitSelection emits a final selectionchange:null at most).
  $effect(() => {
    const eng = engine;
    const s = eng.getSelection();
    sel = s ? { kind: s.kind, count: s.count, bboxWorld: s.bbox } : null;
    vp = eng.getViewport();
    showRestyle = false;
    eng.on('selectionchange', (next) => {
      sel = next;
      if (!next) showRestyle = false;
    });
    // selectionchange does NOT fire on pan/zoom — track the viewport too.
    eng.on('viewport', (v) => { vp = v; });
  });

  let pos = $derived.by(() => {
    if (!sel) return null;
    const b = sel.bboxWorld;
    return {
      x: Math.max(8, (b.x - vp.x) * vp.scale),
      y: Math.max(8, (b.y - vp.y) * vp.scale - BAR_LIFT),
    };
  });

  function copy() {
    const payload = engine.copySelection();
    if (!payload) return;
    app.clipboard = payload;
    app.showToast('Copied — Paste from the toolbar.');
  }
</script>

{#if sel && pos}
  <div class="selection-bar" style="left:{pos.x}px; top:{pos.y}px">
    <!-- Image selections (M4 spec §3): Delete only — no restyle/copy/duplicate. -->
    {#if sel.kind === 'image'}
      <div class="row">
        <button onclick={() => engine.deleteImageSelection()} title="Delete selection">🗑</button>
      </div>
    {:else}
    <div class="row">
      <button onclick={() => engine.deleteSelection()} title="Delete selection">🗑</button>
      <button onclick={() => engine.duplicateSelection()} title="Duplicate">⧉</button>
      <button onclick={copy} title="Copy">⎘</button>
      <button class:active={showRestyle} onclick={() => (showRestyle = !showRestyle)} title="Restyle">🎨</button>
    </div>
    {#if showRestyle}
      <div class="restyle">
        <div class="colors">
          {#each INK_TOKENS as t (t)}
            <button
              class="dot"
              style="background:{resolveInk(t, app.theme)}"
              onclick={() => engine.restyleSelection({ color: t })}
              aria-label={t}
              title={t}
            ></button>
          {/each}
        </div>
        <div class="widths">
          {#each WIDTHS as w (w)}
            <button onclick={() => engine.restyleSelection({ baseWidth: w })} aria-label="Width {w}" title="Width {w}">
              <span class="preview" style="height:{w}px"></span>
            </button>
          {/each}
        </div>
      </div>
    {/if}
    {/if}
  </div>
{/if}

<style>
  .selection-bar { position: absolute; z-index: 5; background: var(--surface); border-radius: var(--radius-panel); box-shadow: var(--shadow-soft); padding: 4px; display: flex; flex-direction: column; gap: 8px; }
  .row { display: flex; gap: 2px; }
  .row button { width: 44px; height: 44px; border: none; border-radius: var(--radius-panel); background: transparent; color: var(--ink-primary); font-size: 17px; cursor: pointer; }
  .row button.active { background: var(--accent); color: #fff; }
  .restyle { display: flex; flex-direction: column; gap: 8px; padding: 0 6px 8px; }
  .colors { display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; }
  .dot { width: 36px; height: 36px; border-radius: 50%; border: 2px solid transparent; cursor: pointer; }
  .widths { display: flex; gap: 4px; }
  .widths button { flex: 1; min-width: 28px; height: 36px; display: grid; place-items: center; border: 1px solid var(--hairline); border-radius: 8px; background: var(--paper); cursor: pointer; }
  .preview { display: block; width: 20px; border-radius: 4px; background: var(--ink-primary); }
</style>
